import api from './api'
import {
  CampoDiagnostico,
  CampoDiagnosticoInput,
  DiagnosticoReserva,
  DiagnosticoRespostaInput,
} from '@/types/estruturacao'

const camposUrl = (produtoId: string) => `/api/v1/produtos/${produtoId}/campos-diagnostico`

/**
 * Diagnóstico da estruturação (Onda 4 / A09): campos por produto (definição, em
 * `originei-produtos`) + respostas/sugestões por reserva (em `originei-oportunidades`).
 *
 * Service PRÓPRIO do A09 — `produtoService`/`oportunidadeService` pertencem a outras
 * frentes e não ganham métodos novos aqui.
 */
export const diagnosticoService = {
  /**
   * Campos do Diagnóstico do produto — só os ativos, que é o que o consultor responde.
   * O cadastro (ADMIN) vive na tela do produto; aqui é leitura.
   */
  camposDoProduto: async (produtoId: string): Promise<CampoDiagnostico[]> => {
    const response = await api.get<CampoDiagnostico[]>(`${camposUrl(produtoId)}?apenasAtivos=true`)
    return (response as unknown as CampoDiagnostico[]) ?? []
  },

  /**
   * Todos os campos, INCLUSIVE os inativos — é a visão do cadastro (ADMIN): sem os
   * inativos não haveria como reativar um campo desligado.
   */
  camposDoProdutoParaCadastro: async (produtoId: string): Promise<CampoDiagnostico[]> => {
    const response = await api.get<CampoDiagnostico[]>(`${camposUrl(produtoId)}?apenasAtivos=false`)
    return (response as unknown as CampoDiagnostico[]) ?? []
  },

  /** Cadastro de um campo (ADMIN). O backend recusa OPCAO sem alternativa. */
  criarCampo: async (
    produtoId: string,
    input: CampoDiagnosticoInput
  ): Promise<CampoDiagnostico> => {
    return (await api.post<CampoDiagnostico>(
      camposUrl(produtoId),
      input
    )) as unknown as CampoDiagnostico
  },

  /** Edição de um campo (ADMIN). Substitui o campo inteiro — é PUT, não PATCH. */
  atualizarCampo: async (
    produtoId: string,
    campoId: string,
    input: CampoDiagnosticoInput
  ): Promise<CampoDiagnostico> => {
    return (await api.put<CampoDiagnostico>(
      `${camposUrl(produtoId)}/${campoId}`,
      input
    )) as unknown as CampoDiagnostico
  },

  /**
   * Remoção definitiva (ADMIN). Para tirar o campo do formulário do consultor SEM perder
   * as respostas já dadas, o caminho é desativar — por isso a tela oferece as duas coisas.
   */
  removerCampo: async (produtoId: string, campoId: string): Promise<void> => {
    await api.delete(`${camposUrl(produtoId)}/${campoId}`)
  },

  /** Respostas confirmadas + bloco de sugestões da IA (nunca gravadas sem confirmação). */
  obter: async (reservaId: string): Promise<DiagnosticoReserva> => {
    return (await api.get<DiagnosticoReserva>(
      `/api/v1/oportunidades/${reservaId}/diagnostico`
    )) as unknown as DiagnosticoReserva
  },

  /**
   * Upsert das respostas. Item com os três valores nulos LIMPA a resposta do campo;
   * campos não citados não são tocados. Autor vem do token, nunca daqui.
   */
  salvar: async (
    reservaId: string,
    respostas: DiagnosticoRespostaInput[]
  ): Promise<DiagnosticoReserva> => {
    return (await api.put<DiagnosticoReserva>(
      `/api/v1/oportunidades/${reservaId}/diagnostico`,
      respostas
    )) as unknown as DiagnosticoReserva
  },

  /**
   * "Captura inteligente · OCR": dispara o reprocessamento de um documento JÁ enviado da
   * reserva pelo pipeline de IA existente. Responde 202 sem corpo — as sugestões chegam
   * de forma assíncrona no GET do diagnóstico; a tela avisa "análise em andamento" e
   * rebusca. Nada é gravado sem o usuário confirmar ("Aplicar").
   */
  sugerir: async (reservaId: string, documentoId: string): Promise<void> => {
    await api.post(`/api/v1/oportunidades/${reservaId}/diagnostico/sugerir`, { documentoId })
  },
}
